import { useState, useEffect } from 'react'
import type { Control, FieldErrors, UseFormRegister } from 'react-hook-form'
import clsx from 'clsx'
import { sanitizeFieldName } from '@/utils/sanitizedLabel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUpload, faFile, faXmark } from '@fortawesome/free-solid-svg-icons'
import type { UploadFileOptionRecord } from '@/lib/types/datocms/graphql/generated'
import Image from 'next/image'
type Props = {
	id: string
	label: string
	required: boolean
	formStyle: string
	colSpan?: string
	register: UseFormRegister<any>
	control: Control<any>
	errors: FieldErrors
	className?: string
	accept?: string
	fileUploadOptions?: UploadFileOptionRecord[]
	hideMainLabel?: boolean
}

export default function FileInput({
	id,
	label,
	required,
	formStyle,
	register,
	errors,
	className,
	accept = 'image/*,application/pdf',
	hideMainLabel = false,
}: Readonly<Props>) {
	const fieldName = sanitizeFieldName(label)
	const [fileName, setFileName] = useState<string>('')
	const [preview, setPreview] = useState<string>('')
	const [, setFileData] = useState<File | null>(null)

	useEffect(() => {
		return () => {
			if (preview) {
				URL.revokeObjectURL(preview)
			}
		}
	}, [preview])

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (file) {
			setFileName(file.name)
			setFileData(file)

			if (file.type.startsWith('image/')) {
				const url = URL.createObjectURL(file)
				setPreview(url)
			} else {
				setPreview('')
			}
		} else {
			setFileName('')
			setPreview('')
			setFileData(null)
		}
	}

	const clearFile = () => {
		setFileName('')
		setPreview('')
		setFileData(null)
		const fileInput = document.getElementById(id) as HTMLInputElement
		if (fileInput) {
			fileInput.value = ''
		}
	}

	return (
		<div className={clsx('form-component col-span-1', className)}>
			{!hideMainLabel && <label className="mb-1">{`${label} ${required ? '*' : ''}`}</label>}

			<div className="relative">
				<div
					className={clsx(
						'form-element-container',
						formStyle === 'line' && 'line-style',
						'cursor-pointer hover:bg-white-frosted'
					)}
					onClick={() => document.getElementById(id)?.click()}
				>
					<input
						id={id}
						type="file"
						accept={accept}
						className="hidden"
						{...register(fieldName, {
							required: required && {
								value: true,
								message: `${label} is required`,
							},
							onChange: handleFileChange,
						})}
					/>

					{/* Choose file / image & name */}

					<div className="w-full aspect-video flex items-center justify-center overflow-hidden mb-2 bg-white rounded-lg">
						{preview ? (
							<Image
								src={preview}
								alt="Preview"
								className="w-full h-full object-cover rounded-lg"
								width={100}
								height={100}
								sizes="20px"
							/>
						) : (
							<div className="flex flex-col items-center gap-3">
								<FontAwesomeIcon
									icon={fileName ? faFile : faUpload}
									className="w-5 h-5 text-gray-400"
								/>
								<span className="flex-1 truncate">{fileName || 'Choose file...'}</span>
							</div>
						)}
					</div>

					{hideMainLabel && <label>{`${label} ${required ? '*' : ''}`}</label>}
				</div>

				{fileName && (
					<button
						type="button"
						onClick={clearFile}
						className="absolute right-7 top-7  hover:bg-gray-100 h-6 w-6 flex items-center justify-center rounded-full"
					>
						<FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
					</button>
				)}
			</div>

			{errors[fieldName] && (
				<span className="mt-1 text-red-500">{errors[fieldName]?.message?.toString()}</span>
			)}
		</div>
	)
}
